import {
  Dialog,
  DialogBackdrop,
  DialogPanel,
  DialogTitle,
} from "@headlessui/react";
import EditIcon from "@mui/icons-material/Edit";
import PriorityHighIcon from "@mui/icons-material/PriorityHigh";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet-async";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import ReactSelect, { components } from "react-select";
import { toast } from "react-toastify";
import { setCredentials } from "../../redux/slices/authSlice";
import { setCompetitorAnalysis } from "../../redux/slices/competitorSlice";
import {
  useCreatePostEditorBlogMutation,
  useDeleteSelectedPostEditorBlogMutation,
  useDeleteSinglePostEditorBlogMutation,
  useGetAllPostEditorBlogQuery,
  useLinkSinglePostEditorToWebsiteMutation,
} from "../../redux/slices/postEditorBlogSlice";
import { useGetAllProjectsQuery } from "../../redux/slices/projectApiSlice";
import { dispatch } from "../../redux/store";
import { H1Header } from "../../shared/components/others";
import { DeleteIcon } from "../../shared/components/others/Icons";
import { trackPlatformUsed } from "../../shared/utils/trackers";
import { DefaultPostEditor } from "./helper";
import { CircularGauge } from "./seoAnalyzer/helper";

// Pour supprimer le séparateur
const NoSeparator = () => null;

const AllPostEditor = () => {
  const { t } = useTranslation();

  // Website init
  const currentProject = useSelector((state) => state?.project);
  const isInitialized = currentProject?.isInitialized;
  const website_url = currentProject?.project?.id
    ? currentProject?.project?.id
    : null;
  const { data: projects } = useGetAllProjectsQuery();

  // Blog post list and functions
  const {
    data: blogPostList,
    isLoading,
    isError,
    refetch,
  } = useGetAllPostEditorBlogQuery(
    { website_url },
    {
      skip: isInitialized === false && currentProject?.project != null,
    }
  );

  const [deletePostEditor, { isLoading: deleteLoading }] =
    useDeleteSinglePostEditorBlogMutation();
  const [deleteSelectedPostEditor, { isLoading: deleteSelectedLoading }] =
    useDeleteSelectedPostEditorBlogMutation();
  const [createPostEditor, { isLoading: createLoading }] =
    useCreatePostEditorBlogMutation();
  const [linkPostEditorToWebsite, { isLoading: linkingLoading }] =
    useLinkSinglePostEditorToWebsiteMutation();

  useEffect(() => {
    dispatch(setCompetitorAnalysis(null));
    if (isInitialized) {
      refetch();
    }
  }, [refetch, isInitialized]);

  const userInfo = useSelector((state) => state?.auth).userInfo;
  const accessToken = useSelector((state) => state?.auth).accessToken;
  const navigate = useNavigate();
  const isReadOnly = userInfo.payment_status === "pending";

  const [open, setOpen] = useState(false);
  const [selectedId, setSelectedId] = useState(null);
  const [deletionType, setDeletionType] = useState("single");
  const [selectedPosts, setSelectedPosts] = useState(new Set());
  const [bulkAction, setBulkAction] = useState("");

  // Gestion dynamique du placement du menu ReactSelect
  const [menuPlacements, setMenuPlacements] = useState({});
  const selectRefs = useRef({});
  const handleMenuOpen = (blogPostId) => () => {
    const ref = selectRefs.current[blogPostId];
    if (ref) {
      const rect = ref.getBoundingClientRect();
      const spaceBelow = window.innerHeight - rect.bottom;
      setMenuPlacements((prev) => ({
        ...prev,
        [blogPostId]: spaceBelow < 220 ? "top" : "auto",
      }));
    }
  };

  const formatDate = (isoDate) => {
    const date = new Date(isoDate);

    const formattedDate = date
      .toISOString()
      .slice(0, 10)
      .split("-")
      .reverse()
      .join("/"); // Format: dd/mm/yyyy
    const formattedTime = date.toISOString().slice(11, 16); // Format: HH:mm

    return `${formattedDate} à ${formattedTime}`;
  };

  const sortedBlogPosts = blogPostList?.data
    ? [...blogPostList.data].sort((a, b) => {
        return (
          new Date(b.last_update_date).getTime() -
          new Date(a.last_update_date).getTime()
        );
      })
    : [];

  const isAllSelected =
    sortedBlogPosts &&
    sortedBlogPosts.length > 0 &&
    sortedBlogPosts.every((post) => selectedPosts.has(post.id));

  const handleDeleteModal = (deleteType, open = true, id = null) => {
    setDeletionType(deleteType);
    setSelectedId(id);
    if (open) {
      setOpen(true);
    } else {
      setOpen(false);
    }
  };

  const handleDeleteSelectedBlogPosts = async () => {
    trackPlatformUsed("blog_post", "delete_bulk_in_db");

    try {
      const res = await deleteSelectedPostEditor(
        Array.from(selectedPosts)
      ).unwrap();
      setSelectedPosts(new Set());
      setOpen(false);
      refetch();
      toast.success(
        t(
          "deleted-selected-blog-posts",
          "Les articles sélectionnés ont bien été supprimés"
        )
      );
    } catch (error) {
      toast.error(
        t(
          "error-deleting-selected-blog-posts",
          "Une erreur a eu lieu lors de la suppression des articles"
        )
      );
    }
  };

  const handleDeleteBlogPost = async (id, modal = false) => {
    trackPlatformUsed("blog_post", "delete_in_db");

    try {
      const res = await deletePostEditor({ id }).unwrap();
      if (modal) {
        setOpen(false);
      }
      refetch();
      toast.success(
        t("deleted-blog-post", "L'article sélectionné a bien été supprimé")
      );
    } catch (error) {
      toast.error(
        t(
          "error-deleting-blog-post",
          "Une erreur a eu lieu lors de la suppression de l'article"
        )
      );
    }
  };

  const handleCreateBlogPost = async () => {
    trackPlatformUsed("blog_post", "create_in_db");
    if (userInfo?.blog_posts_tokens === 0 || !userInfo?.blog_posts_tokens) {
      toast.error(
        t(
          "Upgrade-subscription-to-get-more-tokens",
          "Upgrade subscription to get more tokens"
        )
      );
    } else {
      try {
        const res = await createPostEditor(DefaultPostEditor).unwrap();
        if (res?.status === 201) {
          dispatch(
            setCredentials({
              accessToken: accessToken,
              userInfo: {
                ...userInfo,
                blog_posts_tokens: res?.data?.blog_post_tokens,
              },
            })
          );
          toast.info("article-token-used", "1 Article token used");

          navigate(`/post-editor/${res.data.id}`);
        }
      } catch (err) {
        toast.error(t(err.data.detail));
      }
    }
  };

  // CHECKBOX AND BULK ACTIONS
  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allPostIds = sortedBlogPosts.map((post) => post.id);
      setSelectedPosts(new Set(allPostIds));
    } else {
      setSelectedPosts(new Set());
    }
  };

  const handleCheckboxChange = (postId) => {
    setSelectedPosts((prevSelectedPosts) => {
      const newSelectedPosts = new Set(prevSelectedPosts);
      if (newSelectedPosts.has(postId)) {
        newSelectedPosts.delete(postId);
      } else {
        newSelectedPosts.add(postId);
      }
      return newSelectedPosts;
    });
  };

  const handleApplyAction = async () => {
    if (bulkAction === "delete") {
      handleDeleteModal("selected", true, null);
    }
  };

  // PROJECTS
  const handleProjectChange = async (selectedProject, blogPostId) => {
    try {
      const projectId = selectedProject?.value ? selectedProject.value : null;
      await linkPostEditorToWebsite({ projectId, blogPostId });
      refetch();
    } catch (error) {
      console.error("Error selecting project:", error);
    }
  };

  const projectOptions = [
    {
      value: null, // This will send `null` when selected
      label: (
        <div className="flex items-center gap-2">
          <div className="font-bold text-sm">
            {t("Unselect", "Désélectionner")}
          </div>
        </div>
      ),
    },
    ...(projects?.data?.map((project) => ({
      value: project.id,
      label: (
        <div className="flex items-center gap-2">
          <img src={project.favicon} alt={project.name} className="w-6 h-6" />
          <div className="flex-1 min-w-0">
            <div className="font-bold text-sm">{project.name}</div>
            <div
              className="text-xs text-gray-500 truncate overflow-hidden whitespace-nowrap"
              title={project.url} // Tooltip for full URL
            >
              {project.url}
            </div>
          </div>
        </div>
      ),
    })) || []), // Fallback to an empty array if projects or data is undefined
  ];

  // Pour centrer le favicon dans la valeur sélectionnée
  const CenteredSingleValue = (props) => (
    <components.SingleValue {...props}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          width: "100%",
        }}
      >
        {props.children}
      </div>
    </components.SingleValue>
  );

  // Ajout pour customiser ReactSelect
  const customStyles = {
    menu: (base) => ({
      ...base,
      maxHeight: "200px",
      minWidth: "200px",
      margin: 0,
      padding: 0,
      zIndex: 9999,
      marginTop: "6px", // Ajout d'un espace entre le bouton et le menu
      marginBottom: "8px", // Ajout pour buffer en bas et améliorer le placement auto
    }),
    menuList: (base) => ({
      ...base,
      padding: 0,
      margin: 0,
      overflowY: "auto",
      WebkitOverflowScrolling: "touch",
      maxHeight: "200px",
    }),
    indicatorsContainer: (base) => ({
      ...base,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
    }),
    dropdownIndicator: (base) => ({
      ...base,
      display: "flex",
      justifyContent: "center",
      alignItems: "center",
      width: "100%",
      padding: 0,
      margin: 0,
    }),
  };

  return (
    <div>
      <Helmet>
        <title>{t("blog-posts", "Articles de blog")} | BotBlogR</title>
        <meta
          name="description"
          content="Vision générale de votre compte et de vos activités."
        />
      </Helmet>

      <div className="px-2 pl-6 pr-6">
        <div className="flex items-center justify-between py-3">
          <div>
            <H1Header title={t("My-blog-posts", "Mes articles de blog")} />
            {currentProject?.project && (
              <div className="inline-flex gap-2">
                <img
                  src={currentProject?.project?.favicon}
                  alt={currentProject?.project?.name}
                  className="w-6 h-6"
                />
                <h2 className="font-semibold font-Roboto text-xl">
                  {currentProject?.project?.url}
                </h2>
              </div>
            )}
          </div>
          <div>
            <p className="text-sm font-bold text-center">
              {t("available-tokens", "Available Tokens")} :{" "}
              <span className="text-md text-blue-200">
                {userInfo?.blog_posts_tokens}
              </span>
            </p>
            <button
              className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white rounded font-light"
              disabled={isReadOnly}
              onClick={() => handleCreateBlogPost()}
            >
              {t("create-new-blog-post", "Créer un nouvel article de blog")}
            </button>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 p-4">
        <div className="mx-auto bg-white shadow-md rounded-md overflow-hidden">
          <div className="p-4">
            <table className="w-full text-sm text-left rtl:text-right text-gray-500">
              <thead className="text-xs text-white uppercase bg-blue-100">
                <tr>
                  {[
                    "",
                    t("Website", "Site"),
                    t("Title", "Titre"),
                    t("Target-Request", "Target Request"),
                    t("Score", "Score"),
                    t("Date", "Date"),
                  ].map((heading, index) => (
                    <th
                      key={index}
                      className={`[&:nth-child(3)]:w-[45%] px-6 py-3 ${
                        index === 0
                          ? "rounded-s-lg"
                          : index === 5
                          ? "rounded-e-lg"
                          : ""
                      } text-base col`}
                    >
                      {index === 0 ? (
                        <input
                          type="checkbox"
                          checked={isAllSelected}
                          onChange={handleSelectAll}
                        />
                      ) : (
                        heading
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {sortedBlogPosts?.map((blogPost, index) => (
                  <tr
                    key={blogPost.id}
                    className="odd:bg-white even:bg-black/10 group transition-all"
                  >
                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words">
                      <input
                        type="checkbox"
                        checked={selectedPosts.has(blogPost.id)}
                        onChange={() => handleCheckboxChange(blogPost.id)}
                      />
                    </td>

                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words text-center">
                      <div
                        ref={(el) => {
                          selectRefs.current[blogPost.id] = el;
                        }}
                      >
                        <ReactSelect
                          options={projectOptions}
                          value={
                            blogPost.website && projects?.data
                              ? {
                                  value: projects.data.find(
                                    (obj) => obj.id === blogPost.website
                                  ).id,
                                  label: (
                                    <div className="flex items-center gap-2">
                                      <img
                                        src={
                                          projects.data.find(
                                            (obj) => obj.id === blogPost.website
                                          ).favicon
                                        }
                                        alt={
                                          projects.data.find(
                                            (obj) => obj.id === blogPost.website
                                          ).name
                                        }
                                        className="w-6 h-6"
                                      />
                                    </div>
                                  ),
                                }
                              : null
                          }
                          styles={customStyles}
                          components={{
                            IndicatorSeparator: NoSeparator,
                            SingleValue: CenteredSingleValue,
                          }}
                          menuPlacement={menuPlacements[blogPost.id] || "auto"}
                          menuPosition="fixed"
                          menuPortalTarget={document.body}
                          onMenuOpen={handleMenuOpen(blogPost.id)}
                          onChange={(selectedOption) =>
                            handleProjectChange(selectedOption, blogPost.id)
                          }
                          isSearchable={false}
                          placeholder={"Aucun"}
                        />
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm font-semibold text-gray-900 break-words min-w-[50%] relative">
                      <div className="flex flex-col gap-2">
                        {blogPost.h1_title
                          ? blogPost.h1_title
                          : "(Pas de titre)"}
                        <div className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex gap-4 text-blue-100 text-xs">
                          <button
                            onClick={() => {
                              trackPlatformUsed("blog_post", "edit");
                              navigate(`/post-editor/${blogPost.id}`);
                            }}
                            className="hover:underline flex items-center gap-1"
                          >
                            <EditIcon size={16} /> {t("Edit", "Modifier")}
                          </button>
                          <button
                            onClick={() =>
                              handleDeleteModal("single", true, blogPost.id)
                            }
                            className="hover:underline flex items-center gap-1 text-red-500"
                          >
                            <DeleteIcon size={16} /> {t("Delete", "Supprimer")}
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className="px-6 py-4 text-sm font-medium text-gray-900 break-words whitespace-pre-wrap break-normal">
                      {blogPost.seo_request}
                    </td>

                    <td className="px-6 py-4 text-center">
                      <CircularGauge
                        percentage={blogPost.seo_score ? blogPost.seo_score : 0}
                        isEvenRow={index % 2 !== 0}
                      />
                    </td>

                    <td className="px-6 py-4 text-xs font-medium text-gray-900 break-words">
                      {blogPost.creation_date === blogPost.last_update_date
                        ? "Créé le"
                        : "Dernière modification le"}
                      <br />
                      {formatDate(blogPost.last_update_date)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>

            <div className="flex space-x-4 mt-4">
              <select
                className="border rounded p-2 bg-white"
                value={bulkAction}
                onChange={(e) => setBulkAction(e.target.value)}
              >
                <option value="">{t("bulk-action", "Actions groupées")}</option>
                <option value="delete">{t("delete", "Supprimer")}</option>
              </select>
              <button
                className="px-5 py-[6px] hover:bg-blue-200 *: bg-blue-100 text-white disabled:bg-slate-200 disabled:text-slate-400 rounded font-light"
                onClick={handleApplyAction}
                disabled={selectedPosts.size === 0}
              >
                {t("apply", "Appliquer")}
              </button>
            </div>
          </div>
        </div>
      </div>

      <Dialog
        open={open}
        onClose={() => handleDeleteModal("single", false, null)}
        className="relative z-[999]"
      >
        <DialogBackdrop
          transition
          className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in"
        />

        <div className="fixed inset-0 z-[999] w-screen overflow-y-auto bg-black/50">
          <div className="flex min-h-full items-end justify-center p-4 text-center sm:items-center sm:p-0">
            <DialogPanel
              transition
              className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all data-[closed]:translate-y-4 data-[closed]:opacity-0 data-[enter]:duration-300 data-[leave]:duration-200 data-[enter]:ease-out data-[leave]:ease-in sm:w-full sm:max-w-2xl sm:p-8 data-[closed]:sm:translate-y-0 data-[closed]:sm:scale-95"
            >
              <div className="">
                <DialogTitle
                  as="h3"
                  className="text-base font-semibold leading-6 text-gray-900"
                >
                  <div className="flex items-center gap-3">
                    <div className="mx-auto flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-full bg-error/30 sm:mx-0 sm:h-10 sm:w-10">
                      <PriorityHighIcon
                        aria-hidden="true"
                        className="h-8 w-8 text-error"
                      />
                    </div>
                    <p className="text-base font-semibold leading-6 text-black/40">
                      {deletionType === "single"
                        ? t("delete-blog-post", "Supprimer l'article de blog")
                        : t(
                            "delete-selected-blog-post",
                            "Supprimer les articles de blog sélectionnés"
                          )}
                    </p>
                  </div>
                </DialogTitle>
                <p className="text-base text-black text-left mt-5">
                  {deletionType === "single"
                    ? "Êtes-vous sûr de vouloir supprimer votre article de blog ? Les données seront effacées et vous ne pourrez plus les récupérer."
                    : "Êtes-vous sûr de vouloir supprimer ces articles de blog ? Les données seront effacées et vous ne pourrez plus les récupérer."}
                </p>
                <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                  <button
                    type="button"
                    onClick={
                      deletionType === "single"
                        ? () => handleDeleteBlogPost(selectedId, true)
                        : () => handleDeleteSelectedBlogPosts()
                    }
                    className="inline-flex w-full justify-center rounded-md bg-error px-3 py-2 text-sm font-semibold text-white shadow-sm hover:bg-red-500 sm:ml-3 sm:w-auto"
                  >
                    Supprimer
                  </button>
                  <button
                    type="button"
                    data-autofocus
                    onClick={() => handleDeleteModal("single", false, null)}
                    className="mt-3 inline-flex w-full justify-center rounded-md bg-white px-3 py-2 text-sm font-semibold text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 hover:bg-gray-50 sm:mt-0 sm:w-auto"
                  >
                    Annuler
                  </button>
                </div>
              </div>
            </DialogPanel>
          </div>
        </div>
      </Dialog>
    </div>
  );
};

export default AllPostEditor;
